import {useState, createContext, useMemo, useEffect, useCallback, useContext} from "react";
import PropTypes from "prop-types";

// Hooks
import useMountedState from "../hooks/useMountedState";
import useApi from "../hooks/useApi";
import {useSocket} from "./socket";

// Contexts
import {AuthContext} from "./auth";

// Socket Constants
const STATES_MODIFIED = "notify:states_modified";

export const NavContext = createContext();

const NavProvider = ({children}) => {
  const isMounted = useMountedState();

  const {isAuthenticated} = useContext(AuthContext);

  const socket = useSocket();

  const [online, setOnline] = useState(!!navigator.onLine);
  const [showManageSessions, setShowManageSessions] = useState(false);
  const [idleVisible, setIdleVisible] = useState(false);
  const [allStates, setAllStates] = useState();
  const [showModalState, setShowModalState] = useState(false);
  const [intermediateAction, setIntermediateAction] = useState();

  const {api: apiFacilityState} = useApi("facility-states", {suppress: {error: true}});

  const getStates = useCallback(() => {
    if (isAuthenticated)
      apiFacilityState.callGet().then(({status, data}) => {
        if (status === 200 && data) {
          const stateMap = {};
          data.map(({name, ...rest}) => {
            stateMap[name] = rest;
          });
          setAllStates(stateMap);
        }
      });
  }, [apiFacilityState, isAuthenticated]);

  useEffect(() => {
    if (isMounted() && !allStates) getStates();

    socket.on(STATES_MODIFIED, getStates);

    return () => {
      // unbind all event handlers used in this component
      socket.off(STATES_MODIFIED, getStates);
    };
  }, [isMounted, allStates, socket, getStates]);

  // Make the provider update only when it should.
  // We only want to force re-renders on dependencies
  const memoedValue = useMemo(
    () => ({
      online,
      setOnline,
      showManageSessions,
      setShowManageSessions,
      idleVisible,
      setIdleVisible,
      allStates,
      getStates,
      showModalState,
      setShowModalState,
      intermediateAction,
      setIntermediateAction
    }),
    [idleVisible, online, showManageSessions, allStates, getStates, showModalState, intermediateAction]
  );

  return <NavContext.Provider value={memoedValue}>{children}</NavContext.Provider>;
};

NavProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default NavProvider;
